import { Vector3 } from 'three';
import { clamp, damp, dampAngle } from '../utils/math.js';
import { MASK } from '../physics/groups.js';

const MIN_COLLISION_DISTANCE = 0.55;

/**
 * Third-person chase camera.
 *
 * - Mouse look always has priority (pointer lock, or right-drag fallback).
 * - Follows the vehicle with frame-rate independent smoothing.
 * - Swings in behind the truck while it is moving, and eases back out to a
 *   wider shot at speed.
 * - Pulls in when level geometry blocks the view.
 */
export class CameraRig {
  constructor({ camera, input, physics, config }) {
    this.camera = camera;
    this.input = input;
    this.physics = physics;
    this.config = config;

    this.yaw = config.initialYaw;
    this.pitch = config.initialPitch;
    this.zoom = config.distance;
    this.currentDistance = config.distance;
    this.initialized = false;

    this.followTarget = new Vector3();
    this._desiredTarget = new Vector3();
    this._offset = new Vector3();
    this._rayEnd = new Vector3();
    this._lookIdle = 0;
    this.trauma = 0;
    this._shakeClock = 0;
  }

  /** Add a short camera shake, e.g. when a rocket explodes nearby. */
  addTrauma(amount = 0.4) {
    this.trauma = clamp(this.trauma + amount, 0, 1);
  }

  snapTo(position) {
    this._desiredTarget.set(
      position.x,
      position.y + this.config.targetHeight,
      position.z,
    );
    this.followTarget.copy(this._desiredTarget);
    this.initialized = true;
    this.currentDistance = this.zoom;
    this._lookIdle = 0;
    this.trauma = 0;
    this._shakeClock = 0;
    this.#placeCamera(1 / 60, this.zoom);
  }

  /**
   * @param {number} dt
   * @param {{ position: Vector3, heading: number, speed: number }} focus
   */
  update(dt, focus) {
    const position = focus?.position;
    if (!position) return;

    this._shakeClock += dt;
    this.trauma = Math.max(0, this.trauma - dt * 1.8);

    const look = this.input.takeLookDelta();
    const lookMagnitude = Math.abs(look.x) + Math.abs(look.y);
    this.yaw -= look.x * this.config.sensitivity;
    this.pitch = clamp(
      this.pitch + look.y * this.config.sensitivity,
      this.config.minPitch,
      this.config.maxPitch,
    );

    const wheel = this.input.takeWheel();
    this.zoom = clamp(
      this.zoom + wheel * this.config.zoomSpeed,
      this.config.minDistance,
      this.config.maxDistance,
    );

    const speed = focus.speed ?? 0;
    const speedAbs = Math.abs(speed);

    // Auto-align: keep the camera behind the vehicle while driving, but only
    // after a short grace period so mouse look is never fought.
    if (lookMagnitude > 0) this._lookIdle = 0;
    else this._lookIdle += dt;

    if (this._lookIdle > this.config.alignDelay && speedAbs > this.config.alignSpeed) {
      const strength =
        clamp((speedAbs - this.config.alignSpeed) / 6, 0, 1) * this.config.alignStrength;
      this.yaw = dampAngle(this.yaw, focus.heading + Math.PI, strength, dt);
    }

    this._desiredTarget.set(
      position.x,
      position.y + this.config.targetHeight,
      position.z,
    );

    if (!this.initialized) {
      this.followTarget.copy(this._desiredTarget);
      this.initialized = true;
    } else if (this.followTarget.distanceToSquared(this._desiredTarget) > 64) {
      // The vehicle was teleported (respawn) — snap instead of gliding across
      // the whole level.
      this.followTarget.copy(this._desiredTarget);
    } else {
      const blend = 1 - Math.exp(-this.config.followSpeed * dt);
      this.followTarget.lerp(this._desiredTarget, blend);
    }

    // Ease back a little at speed so more of the world is visible.
    const stretch = 1 + Math.min(speedAbs, 30) * this.config.speedDistance;
    this.#placeCamera(dt, this.zoom * stretch);
  }

  #placeCamera(dt, wantedDistance) {
    const cosPitch = Math.cos(this.pitch);
    this._offset.set(
      Math.sin(this.yaw) * cosPitch,
      Math.sin(this.pitch),
      Math.cos(this.yaw) * cosPitch,
    );

    let distance = wantedDistance;

    this._rayEnd.copy(this.followTarget).addScaledVector(this._offset, wantedDistance);
    const hit = this.physics.raycast(this.followTarget, this._rayEnd, MASK.CAMERA);
    if (hit) {
      distance = Math.max(
        MIN_COLLISION_DISTANCE,
        Math.min(distance, hit.distance - this.config.collisionPadding),
      );
    }

    // Snap inwards immediately, ease back out. This avoids clipping through
    // walls while keeping the camera from popping.
    if (distance < this.currentDistance) {
      this.currentDistance = distance;
    } else {
      this.currentDistance = damp(this.currentDistance, distance, 5, dt);
    }
    this.currentDistance = Math.min(this.currentDistance, wantedDistance);

    this.camera.position.copy(this.followTarget).addScaledVector(this._offset, this.currentDistance);
    this.camera.lookAt(this.followTarget);

    if (this.trauma > 0.001) {
      const strength = this.trauma * this.trauma;
      const t = this._shakeClock;
      this.camera.position.x += Math.sin(t * 73.1) * 0.3 * strength;
      this.camera.position.y += Math.sin(t * 61.7 + 1.7) * 0.22 * strength;
      this.camera.position.z += Math.cos(t * 67.3 + 0.9) * 0.18 * strength;
      this.camera.rotation.z += Math.sin(t * 79.7) * 0.016 * strength;
    }
  }
}
